import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, ProgressBar, Table, Badge } from 'react-bootstrap';
import { FaUpload, FaCheckCircle, FaTimesCircle, FaFilePdf, FaBuilding } from 'react-icons/fa';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4004' 
  : '';

const CargaRecibos = () => {
  const [mes, setMes] = useState('');
  const [secuencia, setSecuencia] = useState('');
  const [empresas, setEmpresas] = useState([]);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState('');
  const [archivos, setArchivos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [resultados, setResultados] = useState(null);
  const [error, setError] = useState(null);

  // Cargar empresas al montar el componente
  useEffect(() => {
    const cargarEmpresas = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/empresas`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setEmpresas(response.data);
      } catch (error) {
        console.error('Error al cargar empresas:', error);
        setError('Error al cargar la lista de empresas');
      }
    };
    cargarEmpresas();
  }, []);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validar que sean PDFs
    const pdfFiles = files.filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
    
    if (pdfFiles.length !== files.length) {
      setError('Solo se permiten archivos PDF');
      return;
    }

    // Validar nombres (formato: YYYYMMSOXXX donde XXX = legajo)
    const archivosConLegajo = pdfFiles.map(file => {
      const nombreSinExt = file.name.replace('.pdf', '');
      const regex = /^(\d{4})(\d{2})(\d{1})O(\d+)$/;
      const match = nombreSinExt.match(regex);
      
      if (match) {
        const [, anio, mesArchivo, secArchivo, legajo] = match;
        return {
          file,
          legajo,
          anio,
          mes: mesArchivo,
          secuencia: secArchivo,
          valido: true,
          error: null
        };
      } else {
        return {
          file,
          legajo: null,
          valido: false,
          error: 'Nombre de archivo inválido. Formato esperado: YYYYMMSOXXX.pdf'
        };
      }
    });

    setArchivos(archivosConLegajo);
    setError(null);
  };

  const subirArchivos = async () => {
    if (!mes || !secuencia) {
      setError('Por favor completa mes y secuencia');
      return;
    }

    if (!empresaSeleccionada) {
      setError('Por favor selecciona una empresa');
      return;
    }

    const archivosValidos = archivos.filter(a => a.valido);
    if (archivosValidos.length === 0) {
      setError('No hay archivos válidos para subir');
      return;
    }

    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      formData.append('mes', mes);
      formData.append('secuencia', secuencia);
      formData.append('cdEmpresa', empresaSeleccionada);
      
      archivosValidos.forEach((archivo, index) => {
        formData.append('recibos', archivo.file);
        formData.append(`legajo_${index}`, archivo.legajo);
      });

      const response = await axios.post(`${API_URL}/api/admin/recibos/cargar`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percentCompleted);
        }
      });

      setResultados(response.data);
    } catch (error) {
      console.error('Error al subir archivos:', error);
      setError(error.response?.data?.message || 'Error al subir los archivos');
    } finally {
      setUploading(false);
    }
  };

  const resetear = () => {
    setArchivos([]);
    setMes('');
    setSecuencia('');
    setEmpresaSeleccionada('');
    setProgress(0);
    setResultados(null);
    setError(null);
    document.getElementById('file-input').value = '';
  };

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h4 className="mb-0">
                <FaUpload className="me-2" />
                Carga Masiva de Recibos
              </h4>
            </Card.Header>
            <Card.Body>
              {/* Configuración inicial */}
              <Row className="mb-4">
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FaBuilding className="me-2" />
                      Empresa
                    </Form.Label>
                    <Form.Select
                      value={empresaSeleccionada}
                      onChange={(e) => setEmpresaSeleccionada(e.target.value)}
                      disabled={uploading}
                    >
                      <option value="">Seleccionar empresa...</option>
                      {empresas.map(empresa => (
                        <option key={empresa.CdEmpresa} value={empresa.CdEmpresa}>
                          {empresa.NmEmpresa}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Mes</Form.Label>
                    <Form.Select
                      value={mes}
                      onChange={(e) => setMes(e.target.value)}
                      disabled={uploading}
                    >
                      <option value="">Seleccionar mes...</option>
                      <option value="01">Enero</option>
                      <option value="02">Febrero</option>
                      <option value="03">Marzo</option>
                      <option value="04">Abril</option>
                      <option value="05">Mayo</option>
                      <option value="06">Junio</option>
                      <option value="07">Julio</option>
                      <option value="08">Agosto</option>
                      <option value="09">Septiembre</option>
                      <option value="10">Octubre</option>
                      <option value="11">Noviembre</option>
                      <option value="12">Diciembre</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Secuencia</Form.Label>
                    <Form.Select
                      value={secuencia}
                      onChange={(e) => setSecuencia(e.target.value)}
                      disabled={uploading}
                    >
                      <option value="">Seleccionar secuencia...</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Selector de archivos */}
              <Form.Group className="mb-4">
                <Form.Label>Archivos PDF</Form.Label>
                <Form.Control
                  id="file-input"
                  type="file"
                  multiple
                  accept=".pdf"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
                <Form.Text className="text-muted">
                  Formato esperado: YYYYMMSOXXX.pdf (ej: 2024121O9054.pdf)
                </Form.Text>
              </Form.Group>

              {/* Mensajes */}
              {error && (
                <Alert variant="danger">
                  {error}
                </Alert>
              )}

              {/* Lista de archivos */}
              {archivos.length > 0 && (
                <Card className="mb-4">
                  <Card.Header>
                    <h6 className="mb-0">Archivos seleccionados ({archivos.length})</h6>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table striped className="mb-0">
                      <thead>
                        <tr>
                          <th>Archivo</th>
                          <th>Legajo</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {archivos.map((archivo, index) => (
                          <tr key={index}>
                            <td>
                              <FaFilePdf className="text-danger me-2" />
                              {archivo.file.name}
                            </td>
                            <td>{archivo.legajo || '-'}</td>
                            <td>
                              {archivo.valido ? (
                                <Badge bg="success">
                                  <FaCheckCircle className="me-1" />
                                  Válido
                                </Badge>
                              ) : (
                                <Badge bg="danger">
                                  <FaTimesCircle className="me-1" />
                                  {archivo.error}
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}

              {/* Progreso de carga */}
              {uploading && (
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Subiendo archivos...</span>
                    <span>{progress}%</span>
                  </div>
                  <ProgressBar now={progress} />
                </div>
              )}

              {/* Resultados */}
              {resultados && (
                <Alert variant={resultados.success ? 'success' : 'danger'}>
                  <h6>Resultado de la carga:</h6>
                  <p className="mb-0">{resultados.message}</p>
                  {resultados.detalles && (
                    <ul className="mb-0 mt-2">
                      {resultados.detalles.map((detalle, index) => (
                        <li key={index}>{detalle}</li>
                      ))}
                    </ul>
                  )}
                </Alert>
              )}

              {/* Botones */}
              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  onClick={subirArchivos}
                  disabled={uploading || archivos.length === 0 || !mes || !secuencia || !empresaSeleccionada}
                >
                  {uploading ? 'Subiendo...' : 'Subir Recibos'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={resetear}
                  disabled={uploading}
                >
                  Resetear
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CargaRecibos;